import {
  archiveOrganizationApi,
  // deleteOrganizationApi,
  unarchiveOrganizationApi,
  useOrganizationDetail,
  useToggleOrganizationPin,
} from "@/api/services/admin/organization";
import { IconStar, IconStarOutline } from "@/assets/icons";
import { IconModeEdit } from "@/assets/icons/IconModeEdit";
// import { IconTrash } from "@/assets/icons/IconTrash";
import { Button } from "@/components/elements/Button";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { MenuNavigate, TMenuContent } from "@/components/elements/MenuNavigate/MenuNavigate";
import { Modal } from "@/components/elements/Modal/Modal";
import { SectionCard } from "@/components/elements/SectionCard";
import { StickyFooter } from "@/components/elements/StickyFooter/StickyFooter";
import { Tab } from "@/components/elements/Tab";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { DATE_FORMAT } from "@/constants/datetime";
import { EOrganizationStatus } from "@/constants/enum";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { EMPTY_STR } from "@/constants/string";
import { OrganizationDetailBasicInfo } from "@/features/organization/components/organization-detail/OrganizationDetailBasicInfo";
import { OrganizationDetailContractList } from "@/features/organization/components/organization-detail/OrganizationDetailContractList";
import { OrganizationDetailOtherInfo } from "@/features/organization/components/organization-detail/OrganizationDetailOtherInfo";
import { OrganizationDetailUserList } from "@/features/organization/components/organization-detail/OrganizationDetailUserList";
import { OrganizationStatusTag } from "@/features/organization/components/OrganizationStatusTag";
import { ORGANIZATION_DETAIL_TAB_KEYS, ORGANIZATION_DETAIL_TAB_OPTIONS } from "@/features/organization/constants/organizationDetail";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { black, text } from "@/theme/colors";
import { dayjs } from "@/utils/dayjs";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { IconButton, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const OrganizationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteDialog = useDialog();
  const archiveDialog = useDialog();

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || ORGANIZATION_DETAIL_TAB_KEYS.BASIC;

  const { data, isLoading } = useOrganizationDetail();
  const { mutate: togglePin } = useToggleOrganizationPin();

  const organization = data?.data;

  const handleBack = () => {
    navigate(AppRoutes.organizations);
  };

  const handleEdit = () => {
    if (!id) return;
    navigate(getDynamicRoute(AppRoutes.organizationEdit, { id }));
  };

  // const handleDelete = async () => {
  //   if (!id) return;
  //   const confirmed = await deleteDialog.show();
  //   if (!confirmed) return;
  //   try {
  //     await deleteOrganizationApi(Number(id));
  //     toast.success(API_SUCCESS_MESSAGE);
  //     await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] });
  //     navigate(AppRoutes.organizations);
  //   } catch (error) {
  //     showError(error);
  //   }
  // };

  const handleArchive = async () => {
    if (!id) return;
    const confirmed = await archiveDialog.show();
    if (!confirmed) return;
    try {
      await archiveOrganizationApi(Number(id));
      toast.success(API_SUCCESS_MESSAGE);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL] });
    } catch (error) {
      showError(error);
    }
  };

  const handleUnarchive = async () => {
    if (!id) return;
    try {
      await unarchiveOrganizationApi(Number(id));
      toast.success(API_SUCCESS_MESSAGE);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_LIST] }),
      ]);
    } catch (error) {
      showError(error);
    }
  };

  const isArchived = organization?.status === EOrganizationStatus.ARCHIVED;
  const isDeleted = organization?.status === EOrganizationStatus.DELETED;

  const canArchive = organization?.canArchive ?? false;

  const handleTogglePin = () => {
    if (!id) return;

    togglePin(Number(id), {
      onSuccess() {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATION.FETCH_DETAIL] });
      },
      onError(error) {
        showError(error);
      },
    });
  };

  const handleTabChange = (_: React.SyntheticEvent, value: string | number) => {
    setSearchParams({ tab: value as string }, { replace: true });
  };

  const basicContractContents: TMenuContent[] = useMemo(() => {
    if (!organization) return [];
    return [
      {
        title: "基本情報",
        content: <OrganizationDetailBasicInfo organization={organization} />,
      },
      {
        title: "その他情報",
        content: <OrganizationDetailOtherInfo organization={organization} />,
      },
      {
        title: "契約一覧",
        content: (
          <SectionCard maxWidth="100%">
            <OrganizationDetailContractList organization={organization} />
          </SectionCard>
        ),
      },
    ];
  }, [organization]);

  if (isLoading) return <LoadingPage />;
  if (!organization) return null;

  const renderTabContent = () => {
    switch (activeTab) {
      case ORGANIZATION_DETAIL_TAB_KEYS.BASIC:
        return (
          <MenuNavigate
            menuPrefix={
              <Typography
                variant="body14Bold"
                sx={{
                  wordBreak: "break-word",
                }}
                color={text.primary}
                px={2}
                pb={1}
              >
                {organization.name}
              </Typography>
            }
            contents={basicContractContents}
            contentAffix={
              <StickyFooter>
                <Stack width="100%" justifyContent="space-between" flexDirection="row">
                  <Button size="sm" variant="ghost" onClick={handleBack}>
                    戻る
                  </Button>
                  <Stack direction="row" gap={2}>
                    {/* Temporarily hiddend */}
                    {/* {isArchived && (
                      <Button size="sm" variant="danger" sx={{ gap: 1 }} onClick={handleDelete}>
                        <IconTrash fontSize="14px" />
                        データ削除
                      </Button>
                    )} */}
                    {!isDeleted && (
                      <>
                        {isArchived ? (
                          <Button size="sm" variant="outline" onClick={handleUnarchive}>
                            アーカイブ解除
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={handleArchive} disabled={!canArchive}>
                            アーカイブ
                          </Button>
                        )}
                      </>
                    )}

                    <Button size="sm" sx={{ gap: 1 }} onClick={handleEdit}>
                      <IconModeEdit color="currentColor" />
                      編集
                    </Button>
                  </Stack>
                </Stack>
              </StickyFooter>
            }
          />
        );
      case ORGANIZATION_DETAIL_TAB_KEYS.USERS:
        return <OrganizationDetailUserList />;
      case ORGANIZATION_DETAIL_TAB_KEYS.AUDIT:
        return (
          <Typography variant="body14" color={text.secondary}>
            監査ログタブの内容
          </Typography>
        );
      default:
        return null;
    }
  };

  return (
    <Stack gap={3}>
      <Stack gap="16px">
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.5}>
          <Typography variant="head20Bold" sx={{ wordBreak: "break-word" }} color={black[30]}>
            {organization?.name}
          </Typography>
          <OrganizationStatusTag status={organization?.status} />
          <IconButton size="small" onClick={handleTogglePin}>
            {organization?.isPinned ? <IconStar /> : <IconStarOutline />}
          </IconButton>
        </Stack>
        <Typography variant="body14" color={text.secondary}>
          組織ID：{organization?.externalId ?? EMPTY_STR.TEXT}　最終ログイン日：
          {organization?.lastLoginAt ? dayjs(organization.lastLoginAt).format(DATE_FORMAT) : EMPTY_STR.DATE}
        </Typography>
      </Stack>

      <Tab value={activeTab} selected={activeTab} options={ORGANIZATION_DETAIL_TAB_OPTIONS} onChange={handleTabChange}>
        {renderTabContent()}
      </Tab>

      <Modal
        isOpen={deleteDialog.open}
        title="組織の削除"
        labelOk="削除する"
        labelClose="キャンセル"
        onOk={deleteDialog.confirm}
        onClose={deleteDialog.cancel}
      >
        <Typography variant="cap12" color={text.secondary} m="8px 0 24px 0" component="div">
          組織内に記録された次の個人情報および機密情報が削除されます。
          <ul>
            <li>ユーザーの一覧</li>
            <li>お客様が入力した全てのデータ</li>
            <li>組織が管理する外国人情報</li>
            <li>各プロダクトの顧客データ</li>
          </ul>
          この操作は取り消すことができませんが、本当によろしいですか？
        </Typography>
      </Modal>

      <Modal
        isOpen={archiveDialog.open}
        title="組織のアーカイブ"
        labelOk="アーカイブする"
        labelClose="キャンセル"
        onOk={archiveDialog.confirm}
        onClose={archiveDialog.cancel}
      >
        <Stack m="8px 0 24px 0">
          <Typography variant="cap12" color={text.secondary}>
            この組織をアーカイブしますか？
          </Typography>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default OrganizationDetailPage;
